import { path } from "../utils";
import { ADX as defaultOptions } from "./defaultOptionsForComputation";

const isValidNumber = (value) =>
  typeof value === "number" && Number.isFinite(value);

export default function () {
  let options = defaultOptions;

  function calculator(data = []) {
    if (!Array.isArray(data) || data.length === 0) return [];

    const { windowSize, smoothingPeriod } = options;
    const period = Math.max(1, windowSize || 1);
    const smoothing = Math.max(1, smoothingPeriod || period);

    const high = path("high");
    const low = path("low");
    const close = path("close");

    let rawTRSum = 0;
    let rawPlusDMSum = 0;
    let rawMinusDMSum = 0;
    let smoothedTR;
    let smoothedPlusDM;
    let smoothedMinusDM;
    let previousADX;
    let dxSum = 0;
    const dxQueue = [];

    const results = [];

    for (let i = 0; i < data.length; i += 1) {
      const current = data[i];

      if (i === 0) {
        results.push(undefined);
        continue;
      }

      const previous = data[i - 1];
      const currentHigh = high(current);
      const currentLow = low(current);
      const currentClose = close(current);
      const previousHigh = high(previous);
      const previousLow = low(previous);
      const previousClose = close(previous);

      if (
        !isValidNumber(currentHigh) ||
        !isValidNumber(currentLow) ||
        !isValidNumber(currentClose) ||
        !isValidNumber(previousHigh) ||
        !isValidNumber(previousLow) ||
        !isValidNumber(previousClose)
      ) {
        results.push(undefined);
        continue;
      }

      const upMove = currentHigh - previousHigh;
      const downMove = previousLow - currentLow;

      let plusDM = 0;
      let minusDM = 0;

      if (upMove > downMove && upMove > 0) {
        plusDM = upMove;
      } else if (downMove > upMove && downMove > 0) {
        minusDM = downMove;
      }

      const trueRange = Math.max(
        currentHigh - currentLow,
        Math.abs(currentHigh - previousClose),
        Math.abs(currentLow - previousClose),
      );

      if (!isValidNumber(trueRange)) {
        results.push(undefined);
        continue;
      }

      if (i <= period) {
        rawTRSum += trueRange;
        rawPlusDMSum += plusDM;
        rawMinusDMSum += minusDM;

        if (i === period) {
          smoothedTR = rawTRSum;
          smoothedPlusDM = rawPlusDMSum;
          smoothedMinusDM = rawMinusDMSum;
        }
      } else {
        smoothedTR = smoothedTR - smoothedTR / period + trueRange;
        smoothedPlusDM = smoothedPlusDM - smoothedPlusDM / period + plusDM;
        smoothedMinusDM = smoothedMinusDM - smoothedMinusDM / period + minusDM;
      }

      if (i < period || !isValidNumber(smoothedTR) || smoothedTR === 0) {
        results.push(undefined);
        continue;
      }

      const plusDI = (smoothedPlusDM / smoothedTR) * 100;
      const minusDI = (smoothedMinusDM / smoothedTR) * 100;
      const diSum = plusDI + minusDI;
      const dx = diSum === 0 ? 0 : (Math.abs(plusDI - minusDI) / diSum) * 100;

      dxQueue.push(dx);
      dxSum += dx;
      if (dxQueue.length > smoothing) {
        dxSum -= dxQueue.shift();
      }

      let adxValue;
      if (previousADX == null) {
        if (dxQueue.length === smoothing) {
          previousADX = dxSum / smoothing;
          adxValue = previousADX;
        }
      } else {
        previousADX = (previousADX * (smoothing - 1) + dx) / smoothing;
        adxValue = previousADX;
      }

      results.push({
        adx: adxValue,
        plusDI,
        minusDI,
        dx,
        histogram: plusDI - minusDI,
      });
    }

    return results;
  }

  calculator.undefinedLength = function () {
    const { windowSize: ws, smoothingPeriod: sp } = options;
    const period = Math.max(1, ws || 1);
    const smoothing = Math.max(1, sp || period);
    return period + smoothing - 1;
  };

  calculator.options = function (x) {
    if (!arguments.length) {
      return options;
    }
    options = { ...defaultOptions, ...x };
    return calculator;
  };

  return calculator;
}
